import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link2, Link2Off, Copy, Library } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Page, Header, Spin } from "@/components/common";
import { CoverFront } from "@/components/CoverViews";

const Book3D = ({ b, i }) => {
  const cv = b.cover, H = 400, W = H * (cv.width_cm - cv.spine_cm) / 2 / cv.height_cm, w = 170, k = w / W;
  return (
    <div className="shrink-0 transition-transform duration-300 hover:-translate-y-3 hover:rotate-[-1deg]" style={{ animation: `fadeUp .5s ease ${i * 0.08}s both` }} data-testid={`shelf-book-${b.id}`}>
      <div className="relative rounded-r-md overflow-hidden shadow-[6px_8px_18px_rgba(0,0,0,.35)]" style={{ width: w, height: H * k }}>
        <div style={{ transform: `scale(${k})`, transformOrigin: "top left", width: W, height: H }}><CoverFront cv={cv} book={b} W={W} H={H} editable={false} /></div>
        <span className="absolute inset-y-0 left-0 w-2.5 bg-gradient-to-r from-black/35 to-transparent" />
      </div>
      <div className="text-xs mt-3 text-center text-[#3b2f2f] font-serif-d max-w-[170px] truncate">{cv.series_no ? `${cv.series_no}. ` : ""}{b.title}</div>
    </div>
  );
};

export function Shelf({ name, books }) {
  return (
    <div className="rounded-2xl p-6 pb-0 bg-[linear-gradient(180deg,#f6efe4,#efe3cf)] border border-[#e2d3b9]" data-testid={`shelf-${name}`}>
      <div className="flex gap-7 overflow-x-auto scroll-thin px-2 pb-4 items-end">{books.map((b, i) => <Book3D key={b.id} b={b} i={i} />)}</div>
      <div className="h-5 -mx-6 bg-[linear-gradient(180deg,#8b5e3c,#5e3b22)] rounded-b-2xl shadow-[0_8px_16px_rgba(94,59,34,.35)]" />
    </div>
  );
}

export default function SeriesShelf() {
  const [list, setList] = useState(null);
  const load = () => api.get("/series").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const link = (t) => `${window.location.origin}/raf/${t}`;
  const share = async (name) => { try { const { data } = await api.post("/series/share", { name }); await navigator.clipboard?.writeText(link(data.token)).catch(() => {}); toast.success("Herkese açık raf linki kopyalandı"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const unshare = async (name) => { await api.post("/series/unshare", { name }); toast.success("Link kapatıldı"); load(); };
  if (!list) return <Page><Spin /></Page>;
  return (
    <Page crumbs={["Seri Rafı"]}>
      <Header title="Seri Rafı" sub="Aynı seriye ait kitaplarınızı raf görünümünde yan yana görün ve okuyucularla paylaşın." />
      {!list.length && <div className="card-z p-8 text-center text-[#5f6f84]" data-testid="shelf-empty"><Library className="mx-auto mb-2 text-teal" />Henüz seri yok. Kapak ekranındaki "Seri Kapak Üretici" ile bir seri oluşturun.</div>}
      <div className="space-y-10">{list.map((s) => (
        <section key={s.name} data-testid={`series-${s.name}`}>
          <div className="flex items-center gap-3 mb-3 flex-wrap"><h2 className="font-serif-d text-2xl text-navy">{s.name}</h2><span className="text-sm text-[#6b7a8c]">{s.books.length} kitap</span>
            <div className="ml-auto flex gap-2 items-center">{s.share_token ? <>
              <input readOnly value={link(s.share_token)} className="inp !py-1.5 text-xs w-[300px]" data-testid={`series-link-${s.name}`} />
              <button onClick={() => { navigator.clipboard?.writeText(link(s.share_token)); toast.success("Kopyalandı"); }} className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1" data-testid={`series-copy-${s.name}`}><Copy size={14} /> Kopyala</button>
              <button onClick={() => unshare(s.name)} className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1 text-[#d1453b]" data-testid={`series-unshare-${s.name}`}><Link2Off size={14} /> Linki kapat</button>
            </> : <button onClick={() => share(s.name)} className="btn-teal px-4 py-1.5 text-sm flex items-center gap-1" data-testid={`series-share-${s.name}`}><Link2 size={14} /> Herkese açık link oluştur</button>}</div></div>
          <Shelf name={s.name} books={s.books} />
        </section>))}</div>
    </Page>
  );
}

export function PublicShelf() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/public/series/${token}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  return (
    <div className="min-h-screen bg-[#fbf7ee] px-6 py-12" data-testid="public-shelf">
      <div className="max-w-6xl mx-auto">
        <div className="font-serif-d text-navy mb-8"><div className="text-3xl font-bold">ZOR</div><div className="text-xs">Dijital Yayınevi</div></div>
        {err && <p className="text-[#d1453b]" data-testid="public-shelf-error">{err}</p>}
        {!d && !err && <Spin />}
        {d && <><h1 className="font-serif-d text-4xl sm:text-5xl text-navy mb-2">{d.name}</h1><p className="text-[#6b7a8c] mb-8">{d.books.length} kitaplık seri</p><Shelf name={d.name} books={d.books} /></>}
      </div>
    </div>
  );
}
